import { observer } from 'mobx-react'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import { FC } from 'react'
import { IJobBase } from '../../../types'
import { getSignInPath, sanitizeHtml, useStores } from '../../../utils'
import { Button } from '../../atoms/Button'
import { EditorContent } from '../../atoms/EditorContent'
import { JobCharacteristicItem } from '../JobCharacteristicItem'
import { JobContentApplicationGuidelines } from '../JobContentApplicationGuidelines'
import styles from './index.module.scss'

type Props = {
  job: IJobBase
}

export const JobContent: FC<Props> = observer(({ job }) => {
  const { t } = useTranslation(['jobs-[job_slug]', 'jobs'])
  const { viewer, ui } = useStores()
  const router = useRouter()
  const signInPath = getSignInPath(router.asPath)

  const openJobApplicationModal = () => {
    if (viewer.isSignedIn) {
      ui.toggleJobApplicationModal()
    } else {
      router.push(signInPath)
    }
  }

  const isCompanyMember = viewer.viewer?.isCompanyMember(job?.company.slug)

  if (!viewer.isSignedIn) {
    const body = job?.body || ''
    const excerptBody = body.slice(0, 189)
    const excerptSuffix = body.length > 190 ? '...' : ''
    const excerpt = `${excerptBody}${excerptSuffix}`
    return (
      <div className={styles.container}>
        <div className={styles.body}>
          <JobCharacteristicItem name={t('募集要項')}>
            <div className={styles.applicationGuidelines}>
              <JobContentApplicationGuidelines job={job} />
            </div>
          </JobCharacteristicItem>
          <JobCharacteristicItem name={t('求人の内容')}>
            <EditorContent sanitizedHtml={sanitizeHtml(excerpt)} />
          </JobCharacteristicItem>
        </div>
        <div className={styles.notSignedInButton}>
          <Button styleType='default' componentType='a' href={signInPath} buttonSize='large'>
            {t('ログインして全て見る')}
          </Button>
        </div>
        <p className={styles.notice}>{t('ログインすると募集の内容を全て閲覧することが出来ます。')}</p>
      </div>
    )
  }

  return (
    <div className={styles.container}>
      <div className={styles.body}>
        <JobCharacteristicItem name={t('募集要項')}>
          <div className={styles.applicationGuidelines}>
            <JobContentApplicationGuidelines job={job} isSignedIn />
          </div>
        </JobCharacteristicItem>
        <JobCharacteristicItem name={t('求人の内容')}>
          <EditorContent sanitizedHtml={sanitizeHtml(job?.body)} />
        </JobCharacteristicItem>
      </div>
      <Button
        styleType='blue'
        componentType='button'
        buttonType='button'
        buttonSize='large'
        disabled={isCompanyMember}
        onClick={openJobApplicationModal}
      >
        {viewer.isSignedIn ? t('応募する') : t('ログインして応募')}
      </Button>
      {isCompanyMember ? (
        <p className={styles.notice}>{t('会社のメンバーは応募できません。')}</p>
      ) : (
        <p className={styles.notice}>{t('「ちょっと話を聞いてみたい」という場合も、気軽に問い合わせてみましょう！')}</p>
      )}
    </div>
  )
})
